import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { CheckIcon, ChevronUpIcon } from 'lucide-react';
import { AutoAwesomeIcon } from '@/shared/ui/Icons';
import arrowForwardIcon from '@/shared/assets/images/arrow-forward.svg';
import { useAiPromptPanel } from './hooks/useAiPromptPanel';
import type { AiPromptPanelProps } from './AiPromptPanel.types';

/**
 * The 1px gradient rule is a padded parent behind an opaque child, so the inner
 * radius is one pixel tighter than the outer one.
 */
const GRADIENT_RULE =
  'rounded-3xl bg-[linear-gradient(96deg,var(--chat-gradient-start),var(--chat-gradient-mid),var(--chat-gradient-end))] p-px shadow-lg';

export const AiPromptPanel = memo<AiPromptPanelProps>(function AiPromptPanel({
  description,
  placeholder,
  agentName,
  agentThumbnailUrl,
  promptValue,
  onPromptChange,
  onSend,
  selectedModel,
  onModelChange,
  isGenerating,
  modelOptions,
}) {
  const { t } = useTranslation('audiences');
  const { textareaRef, handleKeyDown } = useAiPromptPanel({ onSend, isGenerating });

  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  const selectedModelLabel =
    modelOptions.find((o) => o.value === selectedModel)?.label ?? selectedModel;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">{description}</p>

      <div className={GRADIENT_RULE}>
        <div className="flex flex-col rounded-[23px] bg-background">
          <div className="flex items-start gap-3 p-4">
            <div className="mt-1 flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full">
              {agentThumbnailUrl ? (
                <img src={agentThumbnailUrl} alt={agentName} className="size-full object-cover" />
              ) : (
                <AutoAwesomeIcon className="size-4 text-muted-foreground" />
              )}
            </div>

            <textarea
              ref={textareaRef}
              rows={1}
              className="my-1.5 max-h-42 min-h-5 w-full resize-none overflow-y-auto border-none bg-transparent px-0 text-sm leading-5 outline-none field-sizing-content"
              placeholder={placeholder}
              value={promptValue}
              onChange={(e) => onPromptChange(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex items-center justify-between gap-3 px-4 pb-4">
            <DropdownMenu open={modelMenuOpen} onOpenChange={setModelMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isGenerating}>
                  {selectedModelLabel}
                  <ChevronUpIcon
                    className={`transition-transform duration-fast ease-out ${
                      modelMenuOpen ? '' : 'rotate-180'
                    }`}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="min-w-45">
                {modelOptions.map((option) => (
                  <DropdownMenuItem key={option.value} onClick={() => onModelChange(option.value)}>
                    {option.label}
                    {selectedModel === option.value && <CheckIcon className="ml-auto" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              size="icon-sm"
              roundness="full"
              data-testid="send-button"
              aria-label={t('audiences.aiPromptPanel.send')}
              onClick={onSend}
              disabled={isGenerating}
            >
              {isGenerating ? <Spinner /> : <img src={arrowForwardIcon} alt="" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});
